import React from 'react'
import classnames from 'classnames'
import { Link } from 'react-router-dom'
import Panel from '../../components/Panel'
import Badge from '../../components/Badge'
import QueryError from './QueryError'
import ConductorFieldKey from './ConductorFieldKey'
import { formatConductorStage, formatConductorTimestamp, tenantAccountPath } from './conductorUtils'
import { TD, TH } from './conductorTableCells'
import { useGetConductorInstancesQuery, useGetConductorPortfolioQuery } from './conductorApi'

import type { ConductorInstance, ConductorPortfolio } from './conductor.types'

const RECENT_INSTANCES_PAGE_SIZE = 25

const sumValues = (byKey: Record<string, number>): number =>
  Object.values(byKey).reduce((acc, count) => acc + count, 0)

interface SummaryCardProps {
  label: string
  value: React.ReactNode
  detail?: React.ReactNode
  alert?: boolean
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, detail, alert }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
    <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
    <div
      className={classnames(
        'mt-1 text-2xl font-semibold',
        alert === true ? 'text-orange-600 dark:text-orange-400' : 'text-slate-900 dark:text-slate-100'
      )}
    >
      {value}
    </div>
    {detail != null && <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{detail}</div>}
  </div>
)

const SweepVerdictSummary: React.FC<{ sweep: NonNullable<ConductorPortfolio['latestSweep']> }> = ({ sweep }) => (
  <span className="space-x-2">
    <span className="text-green-700 dark:text-green-400">{sweep.verdicts.pass} pass</span>
    <span className="text-red-700 dark:text-red-400">{sweep.verdicts.fail} fail</span>
    <span className="text-slate-500 dark:text-slate-400">{sweep.verdicts.unknown} unknown</span>
  </span>
)

const InstanceRow: React.FC<{ instance: ConductorInstance }> = ({ instance }) => {
  const tenantHref = tenantAccountPath(instance)

  return (
    <tr className="border-t border-slate-100 dark:border-slate-700">
      <TD className="font-medium">
        <Link to={`/conductor/instances/${instance.cuid}`}>{instance.playbookKey}</Link>
      </TD>
      <TD>
        {tenantHref == null ? (
          <span className="font-mono text-xs">{instance.subjectCuid}</span>
        ) : (
          <Link to={tenantHref}>{instance.accountNumber}</Link>
        )}
        <span className="ml-1 text-xs text-slate-400 dark:text-slate-500">{instance.subjectType}</span>
      </TD>
      <TD>{formatConductorStage(instance.currentStage)}</TD>
      <TD>
        <Badge status={instance.status} />
      </TD>
      <TD align="right">{instance.openWorkItems}</TD>
      <TD align="right">{formatConductorTimestamp(instance.modifiedAt)}</TD>
    </tr>
  )
}

const Loading: React.FC = () => (
  <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
)

export const Portfolio: React.FC = () => {
  const portfolioQuery = useGetConductorPortfolioQuery()
  const instancesQuery = useGetConductorInstancesQuery({
    status: 'ACTIVE',
    resultsPerPage: RECENT_INSTANCES_PAGE_SIZE,
  })

  const portfolio = portfolioQuery.data
  const activeInstances = portfolio?.instancesByStatus.ACTIVE ?? 0
  const openWorkItems = portfolio == null ? 0 : portfolio.openWorkItemsByRole.reduce((acc, r) => acc + r.count, 0)
  const outboxStatuses = portfolio == null ? [] : Object.entries(portfolio.outboxByStatus).sort((a, b) => b[1] - a[1])

  return (
    <div className="space-y-4 p-4">
      <div>
        <h2 className="m-0 text-lg font-semibold text-slate-900 dark:text-slate-100">Conductor</h2>
        <p className="m-0 text-xs text-slate-500 dark:text-slate-400">
          Workflow portfolio readiness — gate sweeps, playbook instances, and human work queues
        </p>
        <ConductorFieldKey screen="portfolio" />
      </div>

      {portfolioQuery.isError ? (
        <QueryError message="Failed to load the conductor portfolio." onRetry={() => void portfolioQuery.refetch()} />
      ) : portfolio == null ? (
        <Loading />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryCard
              label="Latest Sweep"
              value={
                portfolio.latestSweep == null ? '—' : <SweepVerdictSummary sweep={portfolio.latestSweep} />
              }
              detail={
                portfolio.latestSweep == null
                  ? 'No sweep has run yet.'
                  : `Evaluated ${formatConductorTimestamp(portfolio.latestSweep.evaluatedAt)}`
              }
            />
            <SummaryCard
              label="Active Workflows"
              value={activeInstances}
              detail={`${sumValues(portfolio.instancesByStatus)} total across all statuses`}
            />
            <Link to="/conductor/inbox" className="block" aria-label="Open the work inbox">
              <SummaryCard
                label="Active Work Items"
                value={openWorkItems}
                detail="Open and in-progress items by role"
              />
            </Link>
            <Link to="/conductor/approvals" className="block" aria-label="Open the approval queue">
              <SummaryCard
                label="Approvals Waiting"
                value={portfolio.approvalsWaiting}
                alert={portfolio.approvalsWaiting > 0}
                detail="Unapproved APPROVE-tier actions"
              />
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Panel header="Active workflows by playbook & stage">
              {portfolio.instancesByPlaybookAndStage.length === 0 ? (
                <div className="py-4 text-sm text-slate-400 dark:text-slate-500">No active workflows.</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr>
                      <TH>Playbook</TH>
                      <TH>Stage</TH>
                      <TH align="right">Instances</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {portfolio.instancesByPlaybookAndStage.map((row) => (
                      <tr
                        key={`${row.playbookKey}:${row.stage}`}
                        className="border-t border-slate-100 dark:border-slate-700"
                      >
                        <TD className="font-medium">{row.playbookKey}</TD>
                        <TD>{row.stage}</TD>
                        <TD align="right">{row.count}</TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>

            <Panel header="Active work items by role">
              {portfolio.openWorkItemsByRole.length === 0 ? (
                <div className="py-4 text-sm text-slate-400 dark:text-slate-500">No active work items.</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr>
                      <TH>Owner role</TH>
                      <TH align="right">Active items</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {portfolio.openWorkItemsByRole.map((row) => (
                      <tr key={row.ownerRole} className="border-t border-slate-100 dark:border-slate-700">
                        <TD className="font-medium">{row.ownerRole}</TD>
                        <TD align="right">{row.count}</TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          </div>

          {outboxStatuses.length > 0 && (
            <Panel header="Outbox status">
              <table className="w-full">
                <thead>
                  <tr>
                    <TH>Status</TH>
                    <TH align="right">Rows</TH>
                  </tr>
                </thead>
                <tbody>
                  {outboxStatuses.map(([status, count]) => (
                    <tr key={status} className="border-t border-slate-100 dark:border-slate-700">
                      <TD className="font-medium">
                        <Link
                          to={`/conductor/outbox?status=${encodeURIComponent(status)}`}
                          className="text-indigo-600 hover:underline dark:text-indigo-400"
                        >
                          {status}
                        </Link>
                      </TD>
                      <TD align="right">{count}</TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}

          <Panel header="Recent active workflow instances">
            {instancesQuery.isError ? (
              <QueryError message="Failed to load workflow instances." onRetry={() => void instancesQuery.refetch()} />
            ) : instancesQuery.data == null ? (
              <Loading />
            ) : instancesQuery.data.results.length === 0 ? (
              <div className="py-4 text-sm text-slate-400 dark:text-slate-500">No active workflow instances.</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <TH>Playbook</TH>
                    <TH>Subject</TH>
                    <TH>Stage</TH>
                    <TH>Status</TH>
                    <TH align="right">Open items</TH>
                    <TH align="right">Last activity</TH>
                  </tr>
                </thead>
                <tbody>
                  {instancesQuery.data.results.map((instance) => (
                    <InstanceRow key={instance.cuid} instance={instance} />
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </>
      )}
    </div>
  )
}

export default Portfolio
